import React, { useMemo } from 'react';
import styles from './DENTToothGrid.module.scss';
import { ToothView } from '../../Enums/ToothView.enum';
import { Scale } from '../../../Base/Interfaces/Scale.interface';
import { Tooth, ToothJaw } from '../../../../graphql/graphql';
import { TOOTH_IMAGE_BASE } from '../../Helpers/periodontalLines/toothImageBase';

interface Props {
	tooth: Tooth;
	view: ToothView;
	scale: Scale;
}

const DENTToothGrid: React.FC<Props> = ({ tooth, view, scale }) => {
	const isUpper = tooth.jaw === ToothJaw.Upper;
	const isBuccal = view === ToothView.BUCCAL;
	const isUp = (isUpper && isBuccal) || (!isUpper && !isBuccal);

	const renderSize = useMemo(() => {
		const renderHeight = TOOTH_IMAGE_BASE.h / scale.vertical;
		const renderWidth = TOOTH_IMAGE_BASE.w / scale.horizontal;
		return {
			w: renderWidth,
			h: Math.min(renderHeight, TOOTH_IMAGE_BASE.h),
		};
	}, [scale]);

	const count = 15;
	const visibleH = renderSize.h;
	const zoom = visibleH / TOOTH_IMAGE_BASE.h;
	const baseStart = 180;
	const startOffset = baseStart * zoom;
	let distance = 20 * zoom;
	const maxNeeded = startOffset + (count - 1) * distance;
	if (maxNeeded > visibleH) {
		distance = (visibleH - startOffset) / (count - 1);
	}

	const lines: number[] = useMemo(() => {
		const off = isUp
			? TOOTH_IMAGE_BASE.h - visibleH + startOffset
			: startOffset;
		return Array.from({ length: count }, (_, i) => off + i * distance);
	}, [isUp, visibleH, startOffset, distance]);

	return (
		<div className={styles.wrapper}>
			<svg viewBox='0 0 280 509' preserveAspectRatio='xMidYMid meet'>
				<g
					transform={
						isUp
							? `translate(0, ${TOOTH_IMAGE_BASE.h}) scale(1, -1)`
							: undefined
					}>
					{lines.map((y, i) => (
						<line
							key={i}
							y1={y}
							y2={y}
							x1='0'
							x2='100%'
							stroke='rgba(0, 0, 0, 0.2)'
							strokeDasharray='7 7'
						/>
					))}
				</g>
			</svg>
		</div>
	);
};

export default DENTToothGrid;
